import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getSprintResumo, getSprintDevs, getProgramados, getSprintDados, getSprintProgramadosDetalhado, clearCache } from "../api/sprint.api";
import TicketDrawer from "../components/TicketDrawer";

const FECHADOS_STATUS = [
  "Fechado", "Resolvido", "Reteste", "Em Teste", "Feedback",
  "Aguardando Testes", "Aguardando Versão", "Ajuste Especificação",
];
const isClosed = (s) => FECHADOS_STATUS.includes(s);
const isEmg = (t) => !t.programado || t.programado === "0" || t.programado === 0 || t.programado === false;
const isBug = (t) => Number(t.tracker_id) === 1;
const REFRESH_SEC = 40;

/* ── Fetch helpers (usados pelo drawer) ────────────── */
// /sprint/programados → inclui subject, desenvolvedor, projeto (Programado='1')
// /sprint/dados       → sem subject; usado apenas para emergências/totais/abertos/fechados
const dados    = () => getSprintDados();
const prog     = () => getSprintProgramadosDetalhado();
const progDone = () => getSprintProgramadosDetalhado().then(d => d.filter(t => isClosed(t.status)));
const progOpen = () => getSprintProgramadosDetalhado().then(d => d.filter(t => !isClosed(t.status)));
const emg      = () => getSprintDados().then(d => d.filter(t => !t.programado || t.programado === "0" || t.programado === 0 || t.programado === false));
const done     = () => getSprintDados().then(d => d.filter(t => isClosed(t.status) || t.fechado === true));
const open_    = () => getSprintDados().then(d => d.filter(t => !isClosed(t.status) && t.fechado !== true));
const emgDone  = () => getSprintDados().then(d => d.filter(t => (!t.programado || t.programado === "0") && (isClosed(t.status) || t.fechado)));
const emgOpen  = () => getSprintDados().then(d => d.filter(t => (!t.programado || t.programado === "0") && !isClosed(t.status) && !t.fechado));
const emgBugsAll  = () => getSprintDados().then(d => d.filter(t => isEmg(t) && isBug(t)));
const emgFuncsAll = () => getSprintDados().then(d => d.filter(t => isEmg(t) && !isBug(t)));
const progAjuste = () => getSprintProgramadosDetalhado().then(d => d.filter(t => t.status === "Ajuste Especificação"));
const tipo1All   = () => getProgramados().then(d => d.filter(t => String(t.programacao_tipo) === "1"));
const tipo2All = () => getProgramados().then(d => d.filter(t => String(t.programacao_tipo) === "2"));

export default function Dashboard() {
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(false);
  const [drawer, setDrawer]   = useState({ open: false, fetchId: 0 });
  const [countdown, setCountdown] = useState(REFRESH_SEC);
  const loadRef = useRef(null);

  // ── Refs para as setas de fluxo (Entrada → Reunião de Entrega) ──
  const flowRef      = useRef(null);
  const totalKpiRef  = useRef(null);
  const progKpiRef   = useRef(null);
  const emgKpiRef    = useRef(null);
  const progTotalRef = useRef(null);
  const emgTotalRef  = useRef(null);
  const emgBugsRef   = useRef(null);
  const emgFuncsRef  = useRef(null);
  const flowLinks = useMemo(() => ([
    { from: totalKpiRef, to: progKpiRef,   color: "#22d3ee", mode: "over", arc: 36 },
    { from: totalKpiRef, to: emgKpiRef,    color: "#f87171", mode: "over", arc: 58 },
    { from: progKpiRef,  to: progTotalRef, color: "#22d3ee" },
    { from: emgKpiRef,   to: emgTotalRef,  color: "#f87171" },
  ]), []);

  const openDrawer = useCallback((cfg) => {
    setDrawer(prev => ({ ...cfg, open: true, fetchId: prev.fetchId + 1 }));
  }, []);
  const closeDrawer = useCallback(() => {
    setDrawer(prev => ({ ...prev, open: false }));
  }, []);

  async function load(isRefresh = false) {
    try {
      setError(false);
      if (isRefresh) clearCache();
      const [resumo, devsResp, ticketsResp, programadosResp, dadosResp] = await Promise.all([
        getSprintResumo(),
        getSprintDevs(),
        getProgramados(),
        getSprintProgramadosDetalhado(),
        getSprintDados(),
      ]);
      if (!resumo?.saida?.geral) throw new Error("Resposta inválida da API");

      // ── Programação: totais via /sprint/devs (Programado='1') ──
      const devs = devsResp?.developers ?? [];
      const progTotal   = devs.reduce((s, d) => s + (d.programado?.total   ?? 0), 0);
      const progFechado = devs.reduce((s, d) => s + (d.programado?.fechado ?? 0), 0);
      const progAberto  = devs.reduce((s, d) => s + (d.programado?.aberto  ?? 0), 0);

      // ── Ajuste de Especificação: contagem via /sprint/programados ──
      const programados = Array.isArray(programadosResp) ? programadosResp : [];
      const progAjusteEspec = programados.filter(t => t.status === "Ajuste Especificação").length;

      // ── Tipo 1 / Tipo 2: breakdown via tickets-programacao (somente entregues/pendentes) ──
      const tickets = Array.isArray(ticketsResp) ? ticketsResp : [];
      const t1 = tickets.filter((t) => String(t.programacao_tipo) === "1");
      const t2 = tickets.filter((t) => String(t.programacao_tipo) === "2");
      const tipo1Fechado = t1.filter((t) => isClosed(t.status)).length;
      const tipo2Fechado = t2.filter((t) => isClosed(t.status)).length;

      // ── Emergências: soma via /sprint/devs ──
      const emgTotal   = devs.reduce((s, d) => s + (d.emergencia?.total   ?? 0), 0);
      const emgFechado = devs.reduce((s, d) => s + (d.emergencia?.fechado ?? 0), 0);
      const emgAberto  = devs.reduce((s, d) => s + (d.emergencia?.aberto  ?? 0), 0);

      // ── Emergências: breakdown Bug vs Funcionalidade via /sprint/dados ──
      const dados = Array.isArray(dadosResp) ? dadosResp : [];
      const emgsData = dados.filter(isEmg);
      const emgBugsCount  = emgsData.filter(isBug).length;
      const emgFuncsCount = emgsData.length - emgBugsCount;

      setData({
        total: resumo.saida.geral.total,
        prog:  resumo.entrada?.programados ?? 0,
        done:  resumo.saida.geral.fechado,
        open:  resumo.saida.geral.aberto,

        progTotal,
        progFechado,
        progAberto,
        progPct: progTotal ? Math.round((progFechado / progTotal) * 100) : 0,
        progAjusteEspec,
        tipo1: { fechado: tipo1Fechado, aberto: t1.length - tipo1Fechado },
        tipo2: { fechado: tipo2Fechado, aberto: t2.length - tipo2Fechado },

        emgTotal,
        emgFechado,
        emgAberto,
        emgPct: emgTotal ? Math.round((emgFechado / emgTotal) * 100) : 0,
        emgBugs:  emgBugsCount,
        emgFuncs: emgFuncsCount,
      });
      // Prefetch silencioso para o drawer (não bloqueia o render)
      Promise.all([getSprintDados(), getSprintProgramadosDetalhado()]).catch(() => {});
    } catch (e) {
      console.error(e);
      setError(true);
    }
  }

  loadRef.current = load;

  useEffect(() => {
    loadRef.current();
    setCountdown(REFRESH_SEC);
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(true); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  if (error) return <ErrorState onRetry={load} />;
  if (!data)  return <DashboardSkeleton />;

  const pct      = data.total ? Math.round((data.done / data.total) * 100) : 0;
  const pctColor = pct >= 70 ? "#4ade80" : pct >= 40 ? "#fbbf24" : "#f87171";

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div style={headerLeft}>
          <h1 style={h1}>Dashboard</h1>
          <span style={sprintBadge}>Sprint Atual</span>
        </div>
        <CountdownRing seconds={countdown} total={REFRESH_SEC} />
      </header>

      {/* ── Fluxo: Visão Geral → Reunião de Entrega ── */}
      <div style={flowWrap} ref={flowRef}>

        {/* ── Visão Geral: ENTRADA / RESULTADO ── */}
        <SectionLabel>Visão Geral</SectionLabel>
        <div style={kpiRow}>

          <KpiGroup label="ENTRADA" colWidths={[1.6, 1, 1]} flex={3} accent="#60a5fa">
            <KpiCard cardRef={totalKpiRef} color="#60a5fa" glow="rgba(96,165,250,0.18)"  symbol="≡" label="Total de Tickets" desc="Todos trabalhados"   value={data.total}    large onClick={() => openDrawer({ title: "Total de Tickets", subtitle: "Todos os tickets trabalhados na sprint", color: "#60a5fa", fetchTickets: dados })}
              breakdown={[
                { value: data.prog,     label: "programados",  color: "#22d3ee" },
                { value: data.emgTotal, label: "emergências",  color: "#f87171" },
              ]}
            />
            <KpiCard cardRef={progKpiRef} color="#22d3ee" glow="rgba(34,211,238,0.18)"  symbol="◎" label="Programados"      desc="Na lista de entrega" value={data.prog}     onClick={() => openDrawer({ title: "Programados", subtitle: "Tickets na lista de entrega", color: "#22d3ee", fetchTickets: prog })} />
            <KpiCard cardRef={emgKpiRef} color="#f87171" glow="rgba(248,113,113,0.18)" symbol="⚡" label="Emergências"      desc="Não planejados"      value={data.emgTotal} onClick={() => openDrawer({ title: "Emergências", subtitle: "Tickets não planejados atendidos na sprint", color: "#f87171", fetchTickets: emg })} />
          </KpiGroup>

          <div style={kpiDivider} />

          <KpiGroup label="RESULTADO" flex={2} accent="#4ade80">
            <KpiCard color="#4ade80" glow="rgba(74,222,128,0.18)"  symbol="✓" label="Resolvidos"  desc="Fechados na sprint"  value={data.done} onClick={() => openDrawer({ title: "Resolvidos", subtitle: "Tickets fechados na sprint atual", color: "#4ade80", fetchTickets: done })} />
            <KpiCard color="#f87171" glow="rgba(248,113,113,0.18)" symbol="!" label="Abertos"     desc="Ainda em andamento"  value={data.open} onClick={() => openDrawer({ title: "Abertos", subtitle: "Tickets ainda em andamento", color: "#f87171", fetchTickets: open_ })} />
          </KpiGroup>

        </div>

        {/* ── Reunião de Entrega ── */}
        <SectionLabel>Reunião de Entrega</SectionLabel>
        <div style={twoCol}>

          <DeliveryCard
            title="Programação da Sprint"
            subtitle="O que estava na lista de entrega para as filiais"
            icon="◎"
            total={data.progTotal}
            fechado={data.progFechado}
            aberto={data.progAberto}
            pct={data.progPct}
            color="#22d3ee"
            glow="rgba(34,211,238,0.15)"
            borderColor="rgba(34,211,238,0.35)"
            tipo1={data.tipo1}
            tipo2={data.tipo2}
            ajusteEspec={data.progAjusteEspec}
            totalRef={progTotalRef}
            onStatClick={{
              total:     () => openDrawer({ title: "Programação — Todos",      subtitle: "Todos os tickets da programação da sprint", color: "#22d3ee", fetchTickets: prog }),
              entregues: () => openDrawer({ title: "Programação — Entregues",  subtitle: "Tickets programados fechados na sprint",    color: "#4ade80", fetchTickets: progDone }),
              pendentes: () => openDrawer({ title: "Programação — Pendentes",  subtitle: "Tickets programados ainda em aberto",       color: "#fbbf24", fetchTickets: progOpen }),
            }}
            onTipoClick={{
              tipo1: () => openDrawer({ title: "Tickets Principais",   subtitle: "Todos os tickets de tipo 1 (principal)", color: "#22d3ee", fetchTickets: tipo1All }),
              tipo2: () => openDrawer({ title: "Tickets Secundários",  subtitle: "Todos os tickets de tipo 2 (secundário)", color: "#a78bfa", fetchTickets: tipo2All }),
            }}
            onAjusteClick={() => openDrawer({ title: "Ajuste de Especificação", subtitle: "Entregues que ainda aguardam revisão de especificação", color: "#fbbf24", fetchTickets: progAjuste })}
          />

          <DeliveryCard
            title="Emergências Atendidas"
            subtitle="Tickets não planejados que impactaram a sprint"
            icon="⚡"
            total={data.emgTotal}
            fechado={data.emgFechado}
            aberto={data.emgAberto}
            pct={data.emgPct}
            color="#f87171"
            glow="rgba(248,113,113,0.15)"
            borderColor="rgba(248,113,113,0.35)"
            bugs={data.emgBugs}
            funcs={data.emgFuncs}
            bugsRef={emgBugsRef}
            funcsRef={emgFuncsRef}
            totalRef={emgTotalRef}
            onStatClick={{
              total:     () => openDrawer({ title: "Emergências — Todas",     subtitle: "Todos os tickets de emergência da sprint", color: "#f87171", fetchTickets: emg }),
              entregues: () => openDrawer({ title: "Emergências — Entregues", subtitle: "Emergências fechadas na sprint",            color: "#4ade80", fetchTickets: emgDone }),
              pendentes: () => openDrawer({ title: "Emergências — Pendentes", subtitle: "Emergências ainda em aberto",               color: "#fbbf24", fetchTickets: emgOpen }),
            }}
            onTrackerClick={{
              bugs:  () => openDrawer({ title: "Emergências — Bugs",         subtitle: "Emergências do tipo Bug",          color: "#f87171", fetchTickets: emgBugsAll }),
              funcs: () => openDrawer({ title: "Emergências — Funcionalidades", subtitle: "Emergências que não são Bug",   color: "#a78bfa", fetchTickets: emgFuncsAll }),
            }}
          />

        </div>

        <FlowArrows containerRef={flowRef} links={flowLinks} />
      </div>

      {/* ── Progresso geral ── */}
      <div style={progressCard}>
        <div style={progressTop}>
          <div>
            <span style={progressTitle}>Progresso Geral da Sprint</span>
            <span style={progressSub}>{data.done} de {data.total} tickets concluídos (programados + emergências)</span>
          </div>
          <span style={{ ...progressPct, color: pctColor }}>{pct}%</span>
        </div>
        <ProgressBar pct={pct} color={pctColor} />
        <div style={progressFooter}>
          <Tag color="#4ade80" label={`${data.done} resolvidos`} />
          <Tag color="#f87171" label={`${data.open} abertos`} />
          <Tag color="#22d3ee" label={`${data.prog} programados`} />
          <Tag color="#f87171" label={`${data.emgTotal} emergências`} />
        </div>
      </div>

      <TicketDrawer
        open={drawer.open}
        onClose={closeDrawer}
        title={drawer.title}
        subtitle={drawer.subtitle}
        color={drawer.color}
        fetchTickets={drawer.fetchTickets}
        fetchId={drawer.fetchId}
      />

    </div>
  );
}

/* ── KPI Group ───────────────────────────────────── */
function KpiGroup({ label, accent, cols = 2, flex = 1, colWidths, children }) {
  const gridTemplateColumns = colWidths
    ? colWidths.map(w => `${w}fr`).join(" ")
    : `repeat(${cols}, 1fr)`;
  return (
    <div style={{ flex, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
      <span style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1.4,
        color: accent,
        textTransform: "uppercase",
        paddingLeft: 2,
        opacity: 0.8,
      }}>
        {label}
      </span>
      <div style={{ display: "grid", gridTemplateColumns, gap: 14 }}>
        {children}
      </div>
    </div>
  );
}

/* ── Flow Arrows (Entrada → Reunião de Entrega) ──── */
function FlowArrows({ containerRef, links }) {
  const [paths, setPaths] = useState([]);

  useEffect(() => {
    let raf = 0;

    function update() {
      const container = containerRef.current;
      if (!container) return;
      const cRect = container.getBoundingClientRect();
      const next = [];
      links.forEach(({ from, to, color, mode, arc }) => {
        if (!from.current || !to.current) return;
        const fRect = from.current.getBoundingClientRect();
        const tRect = to.current.getBoundingClientRect();
        let d;
        if (mode === "over") {
          // Arco por cima, ligando o topo da origem ao topo do destino
          const x1 = fRect.right - fRect.width * 0.15 - cRect.left;
          const y1 = fRect.top - cRect.top;
          const x2 = tRect.left + tRect.width / 2 - cRect.left;
          const y2 = tRect.top - cRect.top;
          const peak = Math.min(y1, y2) - (arc ?? 24);
          d = `M ${x1} ${y1} C ${x1} ${peak}, ${x2} ${peak}, ${x2} ${y2}`;
        } else {
          const x1 = fRect.left + fRect.width / 2 - cRect.left;
          const y1 = fRect.bottom - cRect.top;
          const x2 = tRect.left + tRect.width / 2 - cRect.left;
          const y2 = tRect.top - cRect.top;
          const midY = (y1 + y2) / 2;
          d = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
        }
        next.push({ d, color });
      });
      setPaths(next);
    }

    function schedule() {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    }

    schedule();
    const ro = new ResizeObserver(schedule);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [containerRef, links]);

  return (
    <svg style={flowSvg}>
      <defs>
        {paths.map((p, i) => (
          <marker key={i} id={`flow-arrow-${i}`} markerWidth="8" markerHeight="8" refX="3.5" refY="4" orient="auto">
            <path d="M0,0 L8,4 L0,8 Z" fill={p.color} />
          </marker>
        ))}
      </defs>
      {paths.map((p, i) => (
        <path key={i} d={p.d} fill="none" stroke={p.color} strokeWidth={2} strokeDasharray="5 4" opacity={0.5} markerEnd={`url(#flow-arrow-${i})`} />
      ))}
    </svg>
  );
}

/* ── Countdown Ring ──────────────────────────────── */
function CountdownRing({ seconds, total }) {
  const r      = 11;
  const circ   = 2 * Math.PI * r;
  const filled = circ * (seconds / total);
  const urgent = seconds <= 10;
  const color  = urgent ? "#f87171" : "#60a5fa";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width={30} height={30} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
        <circle cx={15} cy={15} r={r} fill="none" stroke="rgba(96,165,250,0.1)" strokeWidth={2.5} />
        <circle cx={15} cy={15} r={r} fill="none"
          stroke={color} strokeWidth={2.5}
          strokeDasharray={circ}
          strokeDashoffset={circ - filled}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }}
        />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span style={{ color, fontSize: 14, fontWeight: 800, letterSpacing: -0.5 }}>
          {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
        </span>
        <span style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>
          próx. atualiz.
        </span>
      </div>
    </div>
  );
}

/* ── Section Label ───────────────────────────────── */
function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 1.2,
      color: "#475569",
      textTransform: "uppercase",
      paddingLeft: 2,
    }}>
      {children}
    </div>
  );
}

/* ── KPI Card ────────────────────────────────────── */
function KpiCard({ color, glow, symbol, label, desc, value, onClick, breakdown, large, cardRef }) {
  return (
    <div
      ref={cardRef}
      onClick={onClick}
      style={{
        ...card,
        borderTop: `2px solid ${color}`,
        boxShadow: `0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04), 0 -4px 24px ${glow}`,
        cursor: "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06), 0 -4px 28px ${glow}`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)";    e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04), 0 -4px 24px ${glow}`; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ ...iconBox, ...(large ? iconBoxLarge : null), background: glow, border: `1px solid ${color}35`, color }}>
          {symbol}
        </div>
        <span style={{ fontSize: 10, color: `${color}60`, marginTop: 2 }}>▸</span>
      </div>
      <div style={{ ...kpiValue, ...(large ? kpiValueLarge : null) }}>{value ?? "–"}</div>
      <div style={{ ...kpiLabel, ...(large ? kpiLabelLarge : null) }}>{label}</div>
      <div style={kpiDesc}>{desc}</div>
      {breakdown && <KpiBreakdown items={breakdown} />}
    </div>
  );
}

/* ── KPI Breakdown (mostra composição do valor principal) ── */
function KpiBreakdown({ items }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, fontSize: 11, color: "#334155", flexWrap: "wrap" }}>
      <span>=</span>
      {items.map((it, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          {i > 0 && <span>+</span>}
          <span style={{ color: it.color, fontWeight: 800 }}>{it.value}</span>
          <span style={{ color: "#475569" }}>{it.label}</span>
        </span>
      ))}
    </div>
  );
}

/* ── Delivery Card ───────────────────────────────── */
function DeliveryCard({ title, subtitle, icon, total, fechado, aberto, pct, color, glow, borderColor, tipo1, tipo2, ajusteEspec, bugs, funcs, bugsRef, funcsRef, totalRef, onStatClick, onTipoClick, onAjusteClick, onTrackerClick }) {
  const pctColor = pct >= 70 ? "#4ade80" : pct >= 40 ? "#fbbf24" : "#f87171";

  return (
    <div style={{
      ...card,
      borderTop: `2px solid ${color}`,
      boxShadow: `0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04), 0 -4px 24px ${glow}`,
      flex: 1,
      gap: 8,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ ...iconBox, background: glow, border: `1px solid ${borderColor}`, color, flexShrink: 0 }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{title}</div>
          <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>{subtitle}</div>
        </div>
      </div>

      {/* Totais */}
      <div style={{ display: "flex", gap: 6 }}>
        <StatCell label="Total"     value={total}   color="#94a3b8" onClick={onStatClick?.total} cellRef={totalRef} />
        <StatCell label="Entregues" value={fechado}  color="#4ade80" onClick={onStatClick?.entregues} />
        <StatCell label="Pendentes" value={aberto}   color="#f87171" onClick={onStatClick?.pendentes} />
      </div>

      {/* Alerta de Ajuste de Especificação */}
      {ajusteEspec > 0 && (
        <div
          onClick={onAjusteClick}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(251,191,36,0.12)"; e.currentTarget.style.borderColor = "rgba(251,191,36,0.4)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(251,191,36,0.07)"; e.currentTarget.style.borderColor = "rgba(251,191,36,0.25)"; }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "6px 10px",
            borderRadius: 7,
            background: "rgba(251,191,36,0.07)",
            border: "1px solid rgba(251,191,36,0.25)",
            cursor: "pointer",
            transition: "background 0.15s, border-color 0.15s",
          }}
        >
          <span style={{ fontSize: 13, flexShrink: 0 }}>⚠</span>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#fbbf24" }}>
              {ajusteEspec} {ajusteEspec === 1 ? "ticket" : "tickets"} em Ajuste de Especificação
            </span>
            <span style={{ fontSize: 10, color: "#92400e", marginLeft: 6 }}>
              — entregue pelo desenvolvimento, aguardando revisão
            </span>
          </div>
          <span style={{ fontSize: 10, color: "#fbbf2460", marginLeft: "auto", flexShrink: 0 }}>▸</span>
        </div>
      )}

      {/* Breakdown T1 / T2 (só para programação) */}
      {tipo1 && tipo2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <TipoRow
            label="Tickets Principais"
            color="#22d3ee"
            fechado={tipo1.fechado}
            aberto={tipo1.aberto}
            onClick={onTipoClick?.tipo1}
          />
          <TipoRow
            label="Tickets Secundários"
            color="#a78bfa"
            fechado={tipo2.fechado}
            aberto={tipo2.aberto}
            onClick={onTipoClick?.tipo2}
          />
        </div>
      )}

      {/* Breakdown Bug / Funcionalidade (só para emergências) */}
      {bugs != null && funcs != null && (
        <>
          <BranchConnector fromRef={totalRef} leftRef={bugsRef} rightRef={funcsRef} />
          <div style={{ display: "flex", gap: 6 }}>
            <TrackerCell label="Bugs"            value={bugs}  pct={total ? Math.round((bugs  / total) * 100) : 0} color="#f87171" cellRef={bugsRef}  onClick={onTrackerClick?.bugs} />
            <TrackerCell label="Funcionalidades" value={funcs} pct={total ? Math.round((funcs / total) * 100) : 0} color="#a78bfa" cellRef={funcsRef} onClick={onTrackerClick?.funcs} />
          </div>
        </>
      )}

      {/* Progresso */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <ProgressBar pct={pct} color={pctColor} style={{ flex: 1 }} />
        <span style={{ fontSize: 18, fontWeight: 800, color: pctColor, letterSpacing: -0.5, flexShrink: 0 }}>{pct}%</span>
      </div>
    </div>
  );
}

/* ── Tipo Row ────────────────────────────────────── */
function TipoRow({ label, color, fechado, aberto, onClick }) {
  const total = fechado + aberto;
  const pct = total ? Math.round((fechado / total) * 100) : 0;
  return (
    <div
      onClick={onClick}
      onMouseEnter={onClick ? e => { e.currentTarget.style.background = `${color}14`; e.currentTarget.style.borderColor = `${color}38`; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.background = `${color}08`; e.currentTarget.style.borderColor = `${color}20`; } : undefined}
      style={{
      display: "flex",
      alignItems: "center",
      gap: 7,
      padding: "4px 8px",
      borderRadius: 7,
      background: `${color}08`,
      border: `1px solid ${color}20`,
      cursor: onClick ? "pointer" : "default",
      transition: "background 0.15s, border-color 0.15s",
    }}>
      <span style={{
        minWidth: 148,
        padding: "1px 7px",
        borderRadius: 5,
        background: `${color}20`,
        border: `1px solid ${color}40`,
        color,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.5,
        flexShrink: 0,
        textAlign: "center",
      }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${color}99, ${color})`,
          borderRadius: 3,
          transition: "width 0.8s ease",
        }} />
      </div>
      <span style={{ fontSize: 11, color: "#4ade80", fontWeight: 600, flexShrink: 0 }}>{fechado} ✓</span>
      <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>/</span>
      <span style={{ fontSize: 11, color: "#f87171", fontWeight: 600, flexShrink: 0 }}>{aberto} ●</span>
    </div>
  );
}

/* ── Stat Cell ───────────────────────────────────── */
function StatCell({ label, value, color, onClick, cellRef }) {
  return (
    <div
      ref={cellRef}
      onClick={onClick}
      onMouseEnter={onClick ? e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.borderColor = `${color}35`; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; } : undefined}
      style={{
        flex: 1,
        padding: "8px 10px",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 8,
        textAlign: "center",
        cursor: onClick ? "pointer" : "default",
        transition: "background 0.15s, border-color 0.15s",
      }}
    >
      <div style={{ fontSize: 20, fontWeight: 800, color, letterSpacing: -0.5 }}>{value}</div>
      <div style={{ fontSize: 10, color: "#475569", marginTop: 1, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

/* ── Branch Connector (setas locais dentro do card) ─ */
function BranchConnector({ fromRef, leftRef, rightRef }) {
  const containerRef = useRef(null);
  const [paths, setPaths] = useState([]);

  useEffect(() => {
    function update() {
      const container = containerRef.current;
      if (!container || !fromRef?.current || !leftRef?.current || !rightRef?.current) return;
      const cRect = container.getBoundingClientRect();
      const fRect = fromRef.current.getBoundingClientRect();
      const lRect = leftRef.current.getBoundingClientRect();
      const rRect = rightRef.current.getBoundingClientRect();

      const fx  = fRect.left + fRect.width / 2 - cRect.left;
      const fy  = fRect.bottom - cRect.top;
      const lx  = lRect.left + lRect.width / 2 - cRect.left;
      const ly  = lRect.top  - cRect.top;
      const rx  = rRect.left + rRect.width / 2 - cRect.left;
      const ry  = rRect.top  - cRect.top;
      const mid = (fy + Math.min(ly, ry)) / 2;

      setPaths([
        { d: `M ${fx} ${fy} C ${fx} ${mid}, ${lx} ${mid}, ${lx} ${ly}`, color: "#f87171" },
        { d: `M ${fx} ${fy} C ${fx} ${mid}, ${rx} ${mid}, ${rx} ${ry}`, color: "#a78bfa" },
      ]);
    }

    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); window.removeEventListener("resize", update); };
  }, [fromRef, leftRef, rightRef]);

  return (
    <div ref={containerRef} style={{ position: "relative", height: 0, overflow: "visible", zIndex: 10 }}>
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible", pointerEvents: "none" }}>
        <defs>
          {paths.map((p, i) => (
            <marker key={i} id={`bc-arr-${i}`} markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill={p.color} opacity={0.6} />
            </marker>
          ))}
        </defs>
        {paths.map((p, i) => (
          <path key={i} d={p.d} fill="none" stroke={p.color} strokeWidth={1.5} strokeDasharray="4 3" opacity={0.45} markerEnd={`url(#bc-arr-${i})`} />
        ))}
      </svg>
    </div>
  );
}

/* ── Tracker Cell (Bug / Funcionalidade) ─────────── */
function TrackerCell({ label, value, pct, color, onClick, cellRef }) {
  return (
    <div
      ref={cellRef}
      onClick={onClick}
      onMouseEnter={onClick ? e => { e.currentTarget.style.background = `${color}14`; e.currentTarget.style.borderColor = `${color}38`; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.background = `${color}08`; e.currentTarget.style.borderColor = `${color}20`; } : undefined}
      style={{
        flex: 1,
        padding: "8px 10px",
        background: `${color}08`,
        border: `1px solid ${color}20`,
        borderRadius: 8,
        textAlign: "center",
        cursor: onClick ? "pointer" : "default",
        transition: "background 0.15s, border-color 0.15s",
      }}
    >
      <div style={{ fontSize: 20, fontWeight: 800, color, letterSpacing: -0.5 }}>{value}</div>
      {pct != null && (
        <div style={{ fontSize: 11, color: `${color}99`, fontWeight: 700, lineHeight: 1, marginTop: 2 }}>{pct}%</div>
      )}
      <div style={{ fontSize: 10, color: "#475569", marginTop: 2, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

/* ── Progress Bar ────────────────────────────────── */
function ProgressBar({ pct, color, style }) {
  return (
    <div style={{ ...barTrack, ...style }}>
      <div style={{
        ...barFill,
        width: `${pct}%`,
        background: `linear-gradient(90deg, ${color}cc, ${color})`,
        boxShadow: `0 0 12px ${color}60`,
      }} />
    </div>
  );
}

/* ── Tag ─────────────────────────────────────────── */
function Tag({ color, label }) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "4px 10px",
      borderRadius: 20,
      background: `${color}15`,
      border: `1px solid ${color}35`,
      color,
      fontSize: 11,
      fontWeight: 600,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, display: "inline-block" }} />
      {label}
    </span>
  );
}

/* ── Skeleton ────────────────────────────────────── */
function DashboardSkeleton() {
  return (
    <div style={page}>
      <div style={{ ...skelBox, height: 36, width: 220, marginBottom: 28 }} />
      <div style={kpiRow}>
        <div style={{ flex: 3, minWidth: 0, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <div style={{ ...skelBox, height: 148 }} />
          <div style={{ ...skelBox, height: 148 }} />
          <div style={{ ...skelBox, height: 148 }} />
        </div>
        <div style={kpiDivider} />
        <div style={{ flex: 2, minWidth: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ ...skelBox, height: 148 }} />
          <div style={{ ...skelBox, height: 148 }} />
        </div>
      </div>
      <div style={{ ...skelBox, height: 110 }} />
      <div style={twoCol}>
        <div style={{ ...skelBox, height: 240, flex: 1 }} />
        <div style={{ ...skelBox, height: 240, flex: 1 }} />
      </div>
    </div>
  );
}

/* ── Error ───────────────────────────────────────── */
function ErrorState({ onRetry }) {
  return (
    <div style={errorWrap}>
      <div style={errorIcon}>⚠</div>
      <p style={{ color: "#94a3b8", marginBottom: 16 }}>Erro ao carregar dados da API</p>
      <button onClick={onRetry} style={retryBtn}>Tentar novamente</button>
    </div>
  );
}

/* ── Styles ──────────────────────────────────────── */
const page = { width: "100%", display: "flex", flexDirection: "column", gap: 14, paddingBottom: 32, boxSizing: "border-box" };

const header      = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 };
const headerLeft  = { display: "flex", alignItems: "center", gap: 12 };

const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.5 };

const sprintBadge = {
  padding: "3px 12px",
  borderRadius: 20,
  background: "rgba(96,165,250,0.12)",
  border: "1px solid rgba(96,165,250,0.25)",
  color: "#60a5fa",
  fontSize: 12,
  fontWeight: 600,
};


const kpiRow = { display: "flex", gap: 0, alignItems: "flex-start", marginTop: 28 };

const kpiDivider = {
  width: 1,
  alignSelf: "stretch",
  background: "rgba(255,255,255,0.08)",
  margin: "18px 20px 4px",
  flexShrink: 0,
};

const twoCol = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, width: "100%" };

const flowWrap = { position: "relative", display: "flex", flexDirection: "column", gap: 14 };
const flowSvg  = { position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible", pointerEvents: "none", zIndex: 5 };

const card = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 12,
  padding: "14px 16px",
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const iconBox = {
  width: 36,
  height: 36,
  borderRadius: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 16,
  fontWeight: 700,
};

const kpiValue = { fontSize: 32, fontWeight: 800, color: "#f1f5f9", letterSpacing: -1.5, lineHeight: 1, marginTop: 6 };
const kpiLabel = { fontSize: 12, fontWeight: 600, color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const kpiDesc  = { fontSize: 10, color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

const iconBoxLarge  = { width: 44, height: 44, fontSize: 20, borderRadius: 12 };
const kpiValueLarge = { fontSize: 46, marginTop: 8 };
const kpiLabelLarge = { fontSize: 13 };

const progressCard = {
  ...card,
  borderTop: "2px solid rgba(96,165,250,0.3)",
  gap: 8,
};

const progressTop    = { display: "flex", justifyContent: "space-between", alignItems: "flex-end" };
const progressTitle  = { display: "block", fontSize: 13, fontWeight: 700, color: "#e2e8f0" };
const progressSub    = { display: "block", fontSize: 10, color: "#475569", marginTop: 2 };
const progressPct    = { fontSize: 28, fontWeight: 800, letterSpacing: -1 };
const progressFooter = { display: "flex", gap: 6, flexWrap: "wrap" };

const barTrack = { height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 6, overflow: "hidden" };
const barFill  = { height: "100%", borderRadius: 6, transition: "width 0.8s ease" };

const skelBox = { borderRadius: 14, background: "rgba(255,255,255,0.04)", backdropFilter: "blur(18px)" };

const errorWrap = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 8 };
const errorIcon = { fontSize: 40, color: "#fbbf24", marginBottom: 8 };
const retryBtn  = {
  padding: "8px 20px",
  borderRadius: 8,
  border: "1px solid rgba(96,165,250,0.3)",
  background: "rgba(96,165,250,0.1)",
  color: "#60a5fa",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};
